import { useEffect, useRef, useState } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, ArrowLeft, Sparkles, Wand2, RefreshCw, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';
import SnapshotStatusBar from '@/components/SnapshotStatusBar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPost = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  image: '',
  author: 'Whispering Wristwear Team',
  category: 'general',
  published: true
};

export default function BlogAdminPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState(emptyPost);
  const [isNewPost, setIsNewPost] = useState(false);
  const [deleteSlug, setDeleteSlug] = useState(null);
  const [saving, setSaving] = useState(false);

  // AI assist state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState('suggest'); // 'suggest' | 'write'
  const [aiTopics, setAiTopics] = useState([]);
  const [aiLoadingTopics, setAiLoadingTopics] = useState(false);
  const [aiCustomTitle, setAiCustomTitle] = useState('');
  const [aiCustomCategory, setAiCustomCategory] = useState('general');
  const [aiGenerateImage, setAiGenerateImage] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Direct upload state (camera-roll / device file picker)
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [dragHero, setDragHero] = useState(false);
  const [dragInline, setDragInline] = useState(false);
  const heroFileRef = useRef(null);
  const inlineFileRef = useRef(null);
  const contentTextareaRef = useRef(null);

  // Helpers to keep drag/drop handlers terse — only react to actual files
  const hasFiles = (e) =>
    e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');

  const onHeroDragOver = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDragHero(true);
  };
  const onHeroDragLeave = () => setDragHero(false);
  const onHeroDrop = async (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDragHero(false);
    const file = Array.from(e.dataTransfer.files || []).find((f) =>
      (f.type || '').startsWith('image/'),
    );
    await uploadHeroFile(file);
  };

  const onInlineDragOver = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDragInline(true);
  };
  const onInlineDragLeave = () => setDragInline(false);
  const onInlineDrop = async (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDragInline(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) =>
      (f.type || '').startsWith('image/'),
    );
    await uploadInlineFiles(files);
  };

  const categories = [
    { id: 'general', label: 'General' },
    { id: 'history', label: 'History' },
    { id: 'gift-guide', label: 'Gift Guide' },
    { id: 'how-to', label: 'How To' },
    { id: 'behind-the-scenes', label: 'Behind the Scenes' }
  ];

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog?limit=100`);
      setPosts(response.data);
    } catch (error) {
      logger.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setCurrentPost(prev => ({
      ...prev,
      title,
      slug: isNewPost ? generateSlug(title) : prev.slug
    }));
  };

  const handleCreateNew = () => {
    setCurrentPost(emptyPost);
    setIsNewPost(true);
    setIsEditing(true);
  };

  // ── AI assist ──────────────────────────────────────────────────────
  const openAi = (mode) => {
    setAiMode(mode);
    setAiOpen(true);
    if (mode === 'suggest' && aiTopics.length === 0) {
      fetchAiTopics();
    }
  };

  const fetchAiTopics = async () => {
    setAiLoadingTopics(true);
    try {
      const { data } = await axios.post(`${API}/admin/blog/ai/topics`);
      setAiTopics(data.topics || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load suggestions');
    } finally {
      setAiLoadingTopics(false);
    }
  };

  const runAiGenerate = async (title, category) => {
    setAiGenerating(true);
    try {
      // Step 1: text only — usually returns in ~5-10s, well under any proxy timeout
      const { data } = await axios.post(
        `${API}/admin/blog/ai/generate`,
        {
          title,
          category: category || 'general',
          generate_image: false,
        },
        { timeout: 90000 },
      );

      const baseDraft = {
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt,
        content: data.content,
        image: data.image || '',
        author: data.author || 'Lindy at Whispering Wristwear',
        category: data.category || 'general',
        published: true,
      };
      setCurrentPost(baseDraft);
      setIsNewPost(true);
      setIsEditing(true);
      setAiOpen(false);
      setAiCustomTitle('');

      if (!aiGenerateImage) {
        toast.success('Draft ready. Review and save when you\'re happy.');
        return;
      }

      // Step 2: hero image — runs in background, can take ~25-40s. We let
      // the form be edited while it works, and update the image field when
      // it lands. Failure is soft — user can attach an image manually.
      toast.success('Draft ready. Crafting a hero image in the background…');
      try {
        const { data: imgData } = await axios.post(
          `${API}/admin/blog/ai/hero-image`,
          { title: data.title, slug: data.slug },
          { timeout: 180000 },
        );
        if (imgData.status === 'generated' && imgData.image) {
          setCurrentPost((prev) => ({ ...prev, image: imgData.image }));
          toast.success('Hero image attached.');
        } else {
          toast.message('Hero image didn\'t come through — you can add one yourself.');
        }
      } catch (imgErr) {
        toast.message(
          imgErr.response?.data?.detail ||
            'Hero image timed out — the draft is safe; you can add an image manually or retry.',
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed — try again');
    } finally {
      setAiGenerating(false);
    }
  };

  // ---- Direct photo upload (works from phone camera roll on iOS/Android,
  // or the regular file picker on desktop). Endpoint: POST /admin/blog/upload
  const uploadOneFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${API}/admin/blog/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return data.url;
  };

  const handleHeroUpload = async (event) => {
    const file = event.target.files?.[0];
    await uploadHeroFile(file);
    if (heroFileRef.current) heroFileRef.current.value = '';
  };

  const uploadHeroFile = async (file) => {
    if (!file) return;
    setUploadingHero(true);
    try {
      const url = await uploadOneFile(file);
      setCurrentPost((prev) => ({ ...prev, image: url }));
      toast.success('Hero image uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed — please try again');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleInlineUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    await uploadInlineFiles(files);
    if (inlineFileRef.current) inlineFileRef.current.value = '';
  };

  const uploadInlineFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingInline(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const url = await uploadOneFile(file);
        uploaded.push({ url, name: file.name });
      } catch (e) {
        toast.error(
          `${file.name}: ${e?.response?.data?.detail || 'upload failed'}`,
        );
      }
    }
    setUploadingInline(false);
    if (uploaded.length === 0) return;

    // Build markdown snippets — one image per paragraph for clean spacing
    const snippet = uploaded
      .map(
        (u) =>
          `![${u.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ')}](${u.url})`,
      )
      .join('\n\n');

    // Insert at the current cursor position in the content textarea, or
    // append if focus is elsewhere.
    setCurrentPost((prev) => {
      const ta = contentTextareaRef.current;
      const existing = prev.content || '';
      if (!ta) {
        const sep = existing.trim().endsWith('') && existing ? '\n\n' : '';
        return { ...prev, content: existing + sep + snippet + '\n' };
      }
      const start = ta.selectionStart ?? existing.length;
      const end = ta.selectionEnd ?? existing.length;
      const before = existing.slice(0, start);
      const after = existing.slice(end);
      // Ensure a blank line on either side so it renders as its own paragraph
      const leading = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
      const trailing = after && !after.startsWith('\n\n') ? (after.startsWith('\n') ? '\n' : '\n\n') : '';
      const next = before + leading + snippet + trailing + after;
      // Move cursor to just after the inserted snippet on the next tick
      setTimeout(() => {
        if (ta) {
          const pos = (before + leading + snippet).length;
          ta.focus();
          ta.setSelectionRange(pos, pos);
        }
      }, 0);
      return { ...prev, content: next };
    });
    toast.success(
      `Inserted ${uploaded.length} photo${uploaded.length === 1 ? '' : 's'} into the post`,
    );
  };

  const handleEdit = (post) => {
    setCurrentPost(post);
    setIsNewPost(false);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!currentPost.title || !currentPost.slug || !currentPost.excerpt || !currentPost.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      if (isNewPost) {
        await axios.post(`${API}/blog`, currentPost);
        toast.success('Blog post created!');
      } else {
        await axios.put(`${API}/blog/${currentPost.slug}`, currentPost);
        toast.success('Blog post updated!');
      }
      setIsEditing(false);
      fetchPosts();
    } catch (error) {
      logger.error('Error saving post:', error);
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteSlug) return;
    
    try {
      await axios.delete(`${API}/blog/${deleteSlug}`);
      toast.success('Blog post deleted');
      setDeleteSlug(null);
      fetchPosts();
    } catch (error) {
      logger.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="pt-28 pb-16 min-h-screen bg-[#F5F5F0]" data-testid="blog-admin-page">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <AdminBreadcrumb current="Blog" />
        <SnapshotStatusBar />
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="text-caption text-[#84A98C] mb-2">Admin</p>
            <h1 className="heading-display text-3xl text-[#354F52]">
              Blog Manager
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => openAi('suggest')}
              variant="outline"
              className="flex items-center gap-2 border-[#D4AF37]/40 text-[#354F52] hover:bg-[#D4AF37]/10"
              data-testid="ai-suggest-btn"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              Suggest topics
            </Button>
            <Button
              onClick={() => openAi('write')}
              variant="outline"
              className="flex items-center gap-2 border-[#D4AF37]/40 text-[#354F52] hover:bg-[#D4AF37]/10"
              data-testid="ai-write-btn"
            >
              <Wand2 className="w-4 h-4" strokeWidth={1.5} />
              Write from title
            </Button>
            <Button
              onClick={handleCreateNew}
              className="btn-primary flex items-center gap-2"
              data-testid="create-new-post-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              New Post
            </Button>
          </div>
        </div>

        {/* Posts List */}
        <div className="bg-[#FAF9F6] rounded-sm shadow-soft">
          {loading ? (
            <div className="p-8 text-center text-[#84A98C]">Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[#84A98C] mb-4">No blog posts yet</p>
              <Button onClick={handleCreateNew} className="btn-secondary">
                Create Your First Post
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#CAD2C5]/30">
              {posts.map((post) => (
                <div 
                  key={post.slug} 
                  className="p-6 flex flex-col md:flex-row md:items-center gap-4"
                  data-testid={`admin-post-${post.slug}`}
                >
                  {/* Thumbnail */}
                  <div className="flex-shrink-0">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-20 h-20 object-cover rounded-sm"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-serif text-lg text-[#354F52] truncate">
                        {post.title}
                      </h3>
                      {!post.published && (
                        <span className="text-xs bg-[#E09F3E]/20 text-[#E09F3E] px-2 py-0.5 rounded-full">
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#84A98C] mb-1">
                      {post.category.replace('-', ' ')} • {formatDate(post.created_at)}
                    </p>
                    <p className="text-sm text-[#52796F] line-clamp-1">
                      {post.excerpt}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                      className="text-[#84A98C] hover:text-[#354F52]"
                      data-testid={`view-${post.slug}`}
                    >
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(post)}
                      className="text-[#84A98C] hover:text-[#354F52]"
                      data-testid={`edit-${post.slug}`}
                    >
                      <Edit className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteSlug(post.slug)}
                      className="text-[#BC4749] hover:text-[#BC4749]/80"
                      data-testid={`delete-${post.slug}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit/Create Dialog */}
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#354F52]">
                {isNewPost ? 'Create New Post' : 'Edit Post'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title" className="text-[#354F52]">
                  Title *
                </Label>
                <Input
                  id="title"
                  value={currentPost.title}
                  onChange={handleTitleChange}
                  placeholder="Enter post title"
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  data-testid="post-title-input"
                />
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="slug" className="text-[#354F52]">
                  Slug *
                </Label>
                <Input
                  id="slug"
                  value={currentPost.slug}
                  onChange={(e) => setCurrentPost(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="post-url-slug"
                  className="border-[#CAD2C5] focus:border-[#354F52]"
                  disabled={!isNewPost}
                  data-testid="post-slug-input"
                />
                <p className="text-xs text-[#84A98C]">
                  URL: /blog/{currentPost.slug || 'your-post-slug'}
                </p>
              </div>

              {/* Category & Author */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-[#354F52]">
                    Category
                  </Label>
                  <Select
                    value={currentPost.category}
                    onValueChange={(value) => setCurrentPost(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger className="border-[#CAD2C5]" data-testid="post-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="author" className="text-[#354F52]">
                    Author
                  </Label>
                  <Input
                    id="author"
                    value={currentPost.author}
                    onChange={(e) => setCurrentPost(prev => ({ ...prev, author: e.target.value }))}
                    placeholder="Author name"
                    className="border-[#CAD2C5] focus:border-[#354F52]"
                    data-testid="post-author-input"
                  />
                </div>
              </div>

              {/* Image URL */}
              <div
                className="space-y-2"
                onDragOver={onHeroDragOver}
                onDragLeave={onHeroDragLeave}
                onDrop={onHeroDrop}
                data-testid="hero-dropzone"
              >
                <Label htmlFor="image" className="text-[#354F52]">
                  Featured Image
                </Label>
                {dragHero && (
                  <p className="text-xs text-[#D4AF37] uppercase tracking-widest" data-testid="hero-drag-hint">
                    Drop the image to upload
                  </p>
                )}
                <div className={`flex gap-2 flex-wrap items-stretch ${dragHero ? 'ring-2 ring-[#D4AF37] ring-offset-2 ring-offset-[#FAF9F6] rounded-sm' : ''}`}>
                  <Input
                    id="image"
                    value={currentPost.image}
                    onChange={(e) => setCurrentPost(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="Paste a URL or use the upload button →"
                    className="border-[#CAD2C5] focus:border-[#354F52] flex-1 min-w-[200px]"
                    data-testid="post-image-input"
                  />
                  <input
                    ref={heroFileRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleHeroUpload}
                    className="hidden"
                    data-testid="hero-upload-input"
                  />
                  <Button
                    type="button"
                    onClick={() => heroFileRef.current?.click()}
                    disabled={uploadingHero}
                    variant="outline"
                    className="border-[#CAD2C5] text-[#354F52] hover:bg-[#354F52]/5 whitespace-nowrap"
                    data-testid="hero-upload-btn"
                  >
                    {uploadingHero ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading…
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Upload from device
                      </>
                    )}
                  </Button>
                  {currentPost.image && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentPost((p) => ({ ...p, image: '' }))}
                      className="border-[#CAD2C5] text-[#BC4749] hover:bg-[#BC4749]/10"
                      title="Remove hero image"
                      data-testid="hero-remove-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[#84A98C]">
                  JPG, PNG or WebP · up to 10 MB · works straight from your phone&apos;s camera roll
                </p>
                {currentPost.image && (
                  <img
                    src={currentPost.image}
                    alt="Hero preview"
                    className="mt-2 w-full h-48 object-cover rounded-sm"
                    data-testid="hero-preview"
                  />
                )}
              </div>

              {/* Excerpt */}
              <div className="space-y-2">
                <Label htmlFor="excerpt" className="text-[#354F52]">
                  Excerpt * <span className="text-[#84A98C] font-normal">(Short description)</span>
                </Label>
                <Textarea
                  id="excerpt"
                  value={currentPost.excerpt}
                  onChange={(e) => setCurrentPost(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="Brief summary of the post..."
                  className="border-[#CAD2C5] focus:border-[#354F52] min-h-[80px]"
                  data-testid="post-excerpt-input"
                />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <Label htmlFor="content" className="text-[#354F52] pt-2">
                    Content * <span className="text-[#84A98C] font-normal">(Supports ## headings, ### subheadings, - lists, ![alt](url) images)</span>
                  </Label>
                  <div>
                    <input
                      ref={inlineFileRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      multiple
                      onChange={handleInlineUpload}
                      className="hidden"
                      data-testid="inline-upload-input"
                    />
                    <Button
                      type="button"
                      onClick={() => inlineFileRef.current?.click()}
                      disabled={uploadingInline}
                      variant="outline"
                      className="border-[#CAD2C5] text-[#354F52] hover:bg-[#354F52]/5"
                      data-testid="inline-upload-btn"
                    >
                      {uploadingInline ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading…
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Insert photos
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <Textarea
                  id="content"
                  ref={contentTextareaRef}
                  value={currentPost.content}
                  onChange={(e) => setCurrentPost(prev => ({ ...prev, content: e.target.value }))}
                  onDragOver={onInlineDragOver}
                  onDragLeave={onInlineDragLeave}
                  onDrop={onInlineDrop}
                  placeholder="Write your blog post content here... You can also drag photos straight onto this area to insert them."
                  className={`border-[#CAD2C5] focus:border-[#354F52] min-h-[300px] font-mono text-sm ${
                    dragInline ? 'ring-2 ring-[#D4AF37] ring-offset-2 ring-offset-[#FAF9F6]' : ''
                  }`}
                  data-testid="post-content-input"
                />
                <p className="text-xs text-[#84A98C]">
                  Tip: tap &ldquo;Insert photos&rdquo; or simply <strong>drag photos onto the editor</strong> — they&apos;ll be uploaded and inserted at the cursor as <code className="bg-[#F5F5F0] px-1 rounded">![alt](url)</code>.
                </p>
              </div>

              {/* Published Toggle */}
              <div className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-sm">
                <div>
                  <p className="font-medium text-[#354F52]">Published</p>
                  <p className="text-sm text-[#84A98C]">
                    {currentPost.published ? 'Post is visible on the blog' : 'Post is saved as draft'}
                  </p>
                </div>
                <Switch
                  checked={currentPost.published}
                  onCheckedChange={(checked) => setCurrentPost(prev => ({ ...prev, published: checked }))}
                  data-testid="post-published-toggle"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-[#CAD2C5]/30">
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  className="border-[#CAD2C5] text-[#354F52]"
                >
                  <X className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-post-btn"
                >
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {saving ? 'Saving...' : (isNewPost ? 'Create Post' : 'Save Changes')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteSlug} onOpenChange={() => setDeleteSlug(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl text-[#354F52]">
                Delete Blog Post?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#52796F]">
                This action cannot be undone. The post will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#CAD2C5]">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-[#BC4749] hover:bg-[#BC4749]/90 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── AI Assist Modal ─────────────────────────────────────── */}
        <Dialog open={aiOpen} onOpenChange={setAiOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FAF9F6]" data-testid="ai-assist-modal">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#354F52] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                {aiMode === 'suggest' ? 'Pick a topic to write about' : 'Write a post from your own title'}
              </DialogTitle>
            </DialogHeader>

            {/* Tab switcher */}
            <div className="flex gap-2 border-b border-[#CAD2C5]/40 pb-3">
              <button
                onClick={() => setAiMode('suggest')}
                className={`px-3 py-1.5 text-sm rounded-sm ${
                  aiMode === 'suggest'
                    ? 'bg-[#52796F] text-[#FAF9F6]'
                    : 'text-[#52796F] hover:bg-[#F5F5F0]'
                }`}
                data-testid="ai-tab-suggest"
              >
                Suggested topics
              </button>
              <button
                onClick={() => setAiMode('write')}
                className={`px-3 py-1.5 text-sm rounded-sm ${
                  aiMode === 'write'
                    ? 'bg-[#52796F] text-[#FAF9F6]'
                    : 'text-[#52796F] hover:bg-[#F5F5F0]'
                }`}
                data-testid="ai-tab-write"
              >
                Write from title
              </button>
            </div>

            {/* Image toggle (applies to both modes) */}
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-[#354F52]">Auto-generate a hero image</p>
                <p className="text-xs text-[#84A98C]">Runs in the background after the draft loads. Add one yourself if it fails.</p>
              </div>
              <Switch
                checked={aiGenerateImage}
                onCheckedChange={setAiGenerateImage}
                data-testid="ai-image-toggle"
              />
            </div>

            {/* SUGGEST MODE */}
            {aiMode === 'suggest' && (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <button
                    onClick={fetchAiTopics}
                    disabled={aiLoadingTopics}
                    className="text-xs text-[#84A98C] hover:text-[#354F52] flex items-center gap-1"
                    data-testid="ai-refresh-topics"
                  >
                    <RefreshCw className={`w-3 h-3 ${aiLoadingTopics ? 'animate-spin' : ''}`} />
                    {aiLoadingTopics ? 'Thinking…' : 'Refresh ideas'}
                  </button>
                </div>

                {aiLoadingTopics && aiTopics.length === 0 && (
                  <div className="text-sm text-[#84A98C] italic py-6 text-center">
                    Lindy&apos;s ghostwriter is brainstorming…
                  </div>
                )}

                <div className="space-y-2">
                  {aiTopics.map((t, i) => (
                    <div
                      key={i}
                      className="p-4 bg-[#F5F5F0] hover:bg-white border border-transparent hover:border-[#D4AF37]/40 rounded-sm transition-colors"
                      data-testid={`ai-topic-${i}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="font-serif text-base text-[#354F52] leading-snug">{t.title}</p>
                          {t.pitch && (
                            <p className="text-sm text-[#52796F] mt-1.5 leading-relaxed">{t.pitch}</p>
                          )}
                          <p className="text-[10px] tracking-widest uppercase text-[#84A98C] mt-2">
                            {categories.find((c) => c.id === t.category)?.label || t.category}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => runAiGenerate(t.title, t.category)}
                          disabled={aiGenerating}
                          className="bg-[#52796F] hover:bg-[#354F52] text-[#FAF9F6] shrink-0"
                          data-testid={`ai-write-topic-${i}`}
                        >
                          {aiGenerating ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Wand2 className="w-3 h-3 mr-1.5" strokeWidth={1.5} />
                              Write
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WRITE MODE */}
            {aiMode === 'write' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-title" className="text-[#354F52]">Your title</Label>
                  <Input
                    id="ai-title"
                    value={aiCustomTitle}
                    onChange={(e) => setAiCustomTitle(e.target.value)}
                    placeholder="e.g. The morning ritual that shaped my studio"
                    className="border-[#CAD2C5] focus:border-[#354F52]"
                    data-testid="ai-custom-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai-category" className="text-[#354F52]">Category</Label>
                  <Select value={aiCustomCategory} onValueChange={setAiCustomCategory}>
                    <SelectTrigger id="ai-category" className="border-[#CAD2C5]" data-testid="ai-custom-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => runAiGenerate(aiCustomTitle.trim(), aiCustomCategory)}
                  disabled={aiGenerating || aiCustomTitle.trim().length < 4}
                  className="w-full bg-[#52796F] hover:bg-[#354F52] text-[#FAF9F6]"
                  data-testid="ai-generate-from-title"
                >
                  {aiGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Drafting your post…
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Write the post
                    </>
                  )}
                </Button>
                <p className="text-xs text-[#84A98C] text-center">
                  We&apos;ll draft the full post with subheadings, then drop you in the editor to review before publishing.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
